import React, { useState, useEffect, useCallback } from 'react';
import { ActivityIndicator } from 'react-native';
import { useTranslation } from 'react-i18next';
import { FaChartLine, FaExclamationTriangle, FaRocket, FaShieldAlt, FaCheck, FaTimes, FaArrowUp, FaArrowDown, FaBolt, FaPlus, FaTrash, FaSync, FaCrosshairs, FaLightbulb, FaBullseye, FaPlayCircle, FaChevronDown } from 'react-icons/fa';
import ListsManager from '../services/ListsManager';
import { useTheme } from '../theme/ThemeContext';
import { useOrg } from '../hooks/useOrg';

let _t = (s, opts) => (opts && opts.defaultValue) || s;

// Friendly workflow trigger types — hide raw JSON behind human-readable dropdowns.
const TRIGGER_TYPE_OPTIONS = [
  { value: 'pattern_type', labelKey: 'triggerTypePatternMatch', defaultLabel: 'When a pattern is detected' },
  { value: 'category_spike', labelKey: 'triggerTypeCategorySpike', defaultLabel: 'When a category spikes 3x baseline' },
  { value: 'any_high_strength', labelKey: 'triggerTypeHighStrength', defaultLabel: 'Any high-strength pattern' },
  { value: 'custom', labelKey: 'triggerTypeCustom', defaultLabel: 'Custom (edit JSON)' },
];

// Pattern names (from STRATEGIC_LAYER.md pattern types) used for the pattern_type trigger.
const PATTERN_TYPE_OPTIONS = [
  { value: 'volume_spike', labelKey: 'patternVolumeSpike', defaultLabel: 'Volume spike' },
  { value: 'volume_drop', labelKey: 'patternVolumeDrop', defaultLabel: 'Volume drop' },
  { value: 'sentiment_shift', labelKey: 'patternSentimentShift', defaultLabel: 'Sentiment shift' },
  { value: 'engagement_anomaly', labelKey: 'patternEngagementAnomaly', defaultLabel: 'Engagement anomaly' },
  { value: 'competitor_weakness', labelKey: 'patternCompetitorWeakness', defaultLabel: 'Competitor weakness' },
  { value: 'competitor_win', labelKey: 'patternCompetitorWin', defaultLabel: 'Competitor win' },
  { value: 'narrative_convergence', labelKey: 'patternNarrativeConvergence', defaultLabel: 'Narrative convergence' },
];

const URGENCY_RANK = { immediate: 0, hours: 1, days: 2 };
const SEVERITY_RANK = { critical: 0, high: 1, medium: 2, low: 3 };

const firstSentence = (text) => {
  if (!text) return '';
  const m = text.match(/^[\s\S]*?[.!?。！？](\s|$)/);
  return m ? m[0].trim() : text;
};

export default function OrgStrategyScreen({ orgId: propOrgId }) {
  const { theme } = useTheme();
  const { activeOrg } = useOrg();
  const { t, i18n } = useTranslation();
  _t = t;
  const isRTL = i18n.language === 'he';
  const arrow = isRTL ? '←' : '→';
  const orgId = propOrgId || activeOrg?.id;

  const T = {
    bg: theme.bg,
    card: theme.bgCard,
    surface: theme.bgSurface,
    border: theme.border,
    text: theme.textPrimary,
    textSec: theme.textSecondary,
    textMuted: theme.textMuted,
    primary: theme.accentBlue || '#0a84ff',
  };

  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [briefing, setBriefing] = useState(null);
  const [state, setState] = useState(null);
  const [patterns, setPatterns] = useState([]);
  const [priorities, setPriorities] = useState({ current: [], suggested: [] });
  const [newPriority, setNewPriority] = useState({ name: '', keywords: '', weight: 0.7 });
  const [showAddPriority, setShowAddPriority] = useState(false);

  const [assessmentExpanded, setAssessmentExpanded] = useState(false);
  const [showAllActions, setShowAllActions] = useState(false);
  const [showAllPatterns, setShowAllPatterns] = useState(false);

  const [goals, setGoals] = useState([]);
  const [showAddGoal, setShowAddGoal] = useState(false);
  const [newGoal, setNewGoal] = useState({ name: '', description: '', goal_type: 'general', deadline: '', target_metric: { metric: '', target: '', unit: '' } });

  const [workflows, setWorkflows] = useState([]);
  const [showAddWorkflow, setShowAddWorkflow] = useState(false);
  const [workflowAdvanced, setWorkflowAdvanced] = useState(false);
  const [newWorkflow, setNewWorkflow] = useState({ name: '', description: '', trigger_type: 'pattern_type', pattern_type: 'volume_spike', trigger_params: '', steps: [{ order: 1, action: '', owner: '' }], owner_team: '', priority_level: 'medium', cooldown_hours: 24 });

  const [guardrails, setGuardrails] = useState([]);
  const [showAddGuardrail, setShowAddGuardrail] = useState(false);
  const [newGuardrail, setNewGuardrail] = useState({ rule_text: '', rule_type: 'never', scope: 'all', priority: 1 });

  const fetchAll = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    const [briefingRes, stateRes, patternsRes, prioritiesRes, goalsRes, workflowsRes, guardrailsRes] = await Promise.all([
      ListsManager.fetchStrategyBriefing(orgId),
      ListsManager.fetchStrategyState(orgId),
      ListsManager.fetchStrategyPatterns(orgId),
      ListsManager.fetchStrategyPriorities(orgId),
      ListsManager.fetchStrategyGoals(orgId),
      ListsManager.fetchStrategyWorkflows(orgId),
      ListsManager.fetchStrategyGuardrails(orgId),
    ]);
    if (briefingRes?.briefing) setBriefing(briefingRes.briefing);
    if (stateRes?.state) setState(stateRes.state);
    if (patternsRes?.patterns) setPatterns(patternsRes.patterns);
    if (prioritiesRes) setPriorities({ current: prioritiesRes.current || [], suggested: prioritiesRes.suggested || [] });
    if (goalsRes?.goals) setGoals(goalsRes.goals);
    if (workflowsRes?.workflows) setWorkflows(workflowsRes.workflows);
    if (guardrailsRes?.guardrails) setGuardrails(guardrailsRes.guardrails);
    setLoading(false);
  }, [orgId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleGenerate = async () => {
    setGenerating(true);
    const res = await ListsManager.generateStrategyBriefing(orgId);
    if (res?.briefing) setBriefing(res.briefing);
    setGenerating(false);
  };

  const handleDismissPattern = async (patternId) => {
    await ListsManager.dismissStrategyPattern(patternId, orgId);
    setPatterns(prev => prev.filter(p => p.id !== patternId));
  };

  const handleAcceptPriority = async (index) => {
    const res = await ListsManager.acceptSuggestedPriority(orgId, index);
    if (res?.success) {
      setPriorities(prev => {
        const suggested = [...prev.suggested];
        const accepted = suggested.splice(index, 1)[0];
        return { current: [...prev.current, { ...accepted, source: 'user' }], suggested };
      });
    }
  };

  const handleAddPriority = async () => {
    if (!newPriority.name.trim()) return;
    const updated = [...priorities.current, {
      name: newPriority.name,
      keywords: newPriority.keywords.split(',').map(k => k.trim()).filter(Boolean),
      weight: newPriority.weight,
    }];
    const res = await ListsManager.setStrategyPriorities(orgId, updated);
    if (res?.success) {
      setPriorities(prev => ({ ...prev, current: res.priorities || updated }));
      setNewPriority({ name: '', keywords: '', weight: 0.7 });
      setShowAddPriority(false);
    }
  };

  const handleRemovePriority = async (index) => {
    const updated = priorities.current.filter((_, i) => i !== index);
    const res = await ListsManager.setStrategyPriorities(orgId, updated);
    if (res?.success) setPriorities(prev => ({ ...prev, current: res.priorities || updated }));
  };

  // Goals handlers
  const handleAddGoal = async () => {
    if (!newGoal.name.trim()) return;
    const data = {
      name: newGoal.name,
      description: newGoal.description || undefined,
      goal_type: newGoal.goal_type,
      deadline: newGoal.deadline || undefined,
    };
    if (newGoal.target_metric.metric && newGoal.target_metric.target) {
      data.target_metric = { metric: newGoal.target_metric.metric, target: parseFloat(newGoal.target_metric.target), unit: newGoal.target_metric.unit || '' };
    }
    const res = await ListsManager.createStrategyGoal(orgId, data);
    if (res?.goal) {
      setGoals(prev => [...prev, res.goal]);
      setNewGoal({ name: '', description: '', goal_type: 'general', deadline: '', target_metric: { metric: '', target: '', unit: '' } });
      setShowAddGoal(false);
    }
  };

  const handleDeleteGoal = async (goalId) => {
    if (!window.confirm(_t('app.strategy.deleteGoalConfirm', { defaultValue: 'Delete this goal?' }))) return;
    await ListsManager.deleteStrategyGoal(orgId, goalId);
    setGoals(prev => prev.filter(g => g.id !== goalId));
  };

  const handleUpdateGoalStatus = async (goalId, status) => {
    await ListsManager.updateStrategyGoal(orgId, goalId, { status });
    setGoals(prev => prev.filter(g => g.id !== goalId));
  };

  // Workflows handlers
  const handleAddWorkflow = async () => {
    if (!newWorkflow.name.trim() || !newWorkflow.steps[0]?.action) return;
    let params = {};
    if (workflowAdvanced || newWorkflow.trigger_type === 'custom') {
      try { params = newWorkflow.trigger_params ? JSON.parse(newWorkflow.trigger_params) : {}; }
      catch (e) { params = {}; }
    } else if (newWorkflow.trigger_type === 'pattern_type') {
      params = { pattern_type: newWorkflow.pattern_type };
    }
    const triggerType = newWorkflow.trigger_type === 'custom' ? 'pattern_type' : newWorkflow.trigger_type;
    const data = {
      name: newWorkflow.name,
      description: newWorkflow.description || undefined,
      trigger_condition: { type: triggerType, params },
      steps: newWorkflow.steps.filter(s => s.action.trim()),
      owner_team: newWorkflow.owner_team || undefined,
      priority_level: newWorkflow.priority_level,
      cooldown_hours: newWorkflow.cooldown_hours,
    };
    const res = await ListsManager.createStrategyWorkflow(orgId, data);
    if (res?.workflow) {
      setWorkflows(prev => [...prev, res.workflow]);
      setNewWorkflow({ name: '', description: '', trigger_type: 'pattern_type', pattern_type: 'volume_spike', trigger_params: '', steps: [{ order: 1, action: '', owner: '' }], owner_team: '', priority_level: 'medium', cooldown_hours: 24 });
      setWorkflowAdvanced(false);
      setShowAddWorkflow(false);
    }
  };

  const handleDeleteWorkflow = async (workflowId) => {
    if (!window.confirm(_t('app.strategy.deleteWorkflowConfirm', { defaultValue: 'Delete this workflow?' }))) return;
    await ListsManager.deleteStrategyWorkflow(orgId, workflowId);
    setWorkflows(prev => prev.filter(w => w.id !== workflowId));
  };

  // Guardrails handlers
  const handleAddGuardrail = async () => {
    if (!newGuardrail.rule_text.trim()) return;
    const res = await ListsManager.createStrategyGuardrail(orgId, newGuardrail);
    if (res?.guardrail) {
      setGuardrails(prev => [...prev, res.guardrail]);
      setNewGuardrail({ rule_text: '', rule_type: 'never', scope: 'all', priority: 1 });
      setShowAddGuardrail(false);
    }
  };

  const handleDeleteGuardrail = async (guardrailId) => {
    if (!window.confirm(_t('app.strategy.deleteGuardrailConfirm', { defaultValue: 'Delete this guardrail?' }))) return;
    await ListsManager.deleteStrategyGuardrail(orgId, guardrailId);
    setGuardrails(prev => prev.filter(g => g.id !== guardrailId));
  };

  if (!orgId) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: T.bg }}>
        <span style={{ color: T.textMuted, fontSize: 14 }}>{_t('app.strategy.selectOrganization', { defaultValue: 'Select an organization to view strategy' })}</span>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: T.bg }}>
        <ActivityIndicator size="large" color={T.primary} />
      </div>
    );
  }

  const momentum = state?.momentum_7d?.brand || {};
  const competitive = state?.competitive_position || {};
  const baselines = state?.performance_baselines?.brand || {};

  // Intelligence: sort + filter for the daily glance.
  const opportunities = [...(briefing?.opportunities || [])].sort((a, b) => (URGENCY_RANK[a.urgency] ?? 9) - (URGENCY_RANK[b.urgency] ?? 9));
  const threats = [...(briefing?.threats || [])].sort((a, b) => (SEVERITY_RANK[a.severity] ?? 9) - (SEVERITY_RANK[b.severity] ?? 9));
  const actions = briefing?.recommended_actions || [];
  const topAction = actions[0];
  const visibleActions = showAllActions ? actions : actions.slice(0, 3);
  const strongPatterns = patterns.filter(p => (p.strength || 0) > 0.4);
  const visiblePatterns = showAllPatterns ? strongPatterns : strongPatterns.slice(0, 4);

  const assessment = briefing?.situation_assessment || '';
  const assessmentShort = firstSentence(assessment);
  const assessmentHasMore = assessment.length > assessmentShort.length;

  return (
    <div style={{ background: T.bg, padding: '36px 40px', direction: isRTL ? 'rtl' : 'ltr', maxWidth: 1280, margin: '0 auto' }}>
      <style dangerouslySetInnerHTML={{ __html: `
        .strat-card { border-radius: 18px; border: 1px solid ${T.border}; background: ${T.card}; padding: 28px; transition: border-color 0.2s ease; }
        .strat-card:hover { border-color: ${T.textMuted}33; }
        .strat-section-title { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; color: ${T.textMuted}; margin-bottom: 18px; display: flex; align-items: center; gap: 8px; }
        .strat-strength-bar { height: 3px; border-radius: 3px; background: ${T.textMuted}1f; overflow: hidden; }
        .strat-strength-fill { height: 100%; border-radius: 3px; transition: width 0.4s ease; }
        .strat-btn { border: none; cursor: pointer; border-radius: 10px; font-size: 13px; font-weight: 600; padding: 10px 18px; display: inline-flex; align-items: center; gap: 7px; transition: all 0.18s ease; }
        .strat-btn:hover { opacity: 0.88; }
        .strat-input { border: 1px solid ${T.border}; border-radius: 10px; padding: 11px 14px; font-size: 13px; background: ${T.surface}; color: ${T.text}; outline: none; width: 100%; transition: border-color 0.15s ease; }
        .strat-input:focus { border-color: ${T.primary}; }
        .strat-collapse-head { width: 100%; background: none; border: none; cursor: pointer; padding: 0; margin-bottom: 0; }
        .strat-link { background: none; border: none; cursor: pointer; font-size: 12px; font-weight: 600; color: ${T.primary}; padding: 10px 0 0; }
        .strat-row { padding: 14px 16px; border-radius: 12px; background: ${T.surface}; transition: background 0.15s ease; }
      ` }} />

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 32 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, color: T.text, letterSpacing: -0.6 }}>{_t('app.strategy.title', { defaultValue: 'Strategy' })}</h1>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: T.textMuted }}>
            {activeOrg?.name} · {_t('app.strategy.subtitle', { defaultValue: 'Strategic intelligence & recommendations' })}
          </p>
        </div>
        <button
          className="strat-btn"
          onClick={handleGenerate}
          disabled={generating}
          style={{ background: T.primary, color: '#fff', opacity: generating ? 0.7 : 1 }}
        >
          {generating ? <FaSync size={11} className="fa-spin" /> : <FaBolt size={11} />}
          {generating ? _t('app.strategy.generating', { defaultValue: 'Generating...' }) : _t('app.strategy.newBriefing', { defaultValue: 'New Briefing' })}
        </button>
      </div>

      {/* ═══ HERO: at-a-glance summary (momentum + assessment + top action) ═══ */}
      <div className="strat-card" style={{ marginBottom: 28 }}>
        {/* Momentum chips */}
        {(momentum.direction || momentum.sentiment_delta !== undefined || baselines.avg_daily_signals > 0) && (
          <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
            {momentum.direction && (
              <MomentumChip label={_t('app.strategy.momentumLabel', { defaultValue: 'Momentum' })} value={momentum.direction} delta={momentum.volume_delta_pct} T={T} />
            )}
            {momentum.sentiment_delta !== undefined && (
              <MomentumChip label={_t('app.strategy.sentimentLabel', { defaultValue: 'Sentiment' })} value={momentum.sentiment_delta > 0 ? _t('app.strategy.improving', { defaultValue: 'improving' }) : momentum.sentiment_delta < -0.05 ? _t('app.strategy.declining', { defaultValue: 'declining' }) : _t('app.strategy.stable', { defaultValue: 'stable' })} delta={momentum.sentiment_delta * 100} T={T} isSentiment />
            )}
            {baselines.avg_daily_signals > 0 && (
              <div style={{ padding: '10px 16px', borderRadius: 10, background: T.surface, border: `1px solid ${T.border}` }}>
                <span style={{ fontSize: 11, color: T.textMuted }}>{_t('app.strategy.avgSignalsPerDay', { defaultValue: 'Avg signals/day' })}</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: T.text, marginLeft: 8 }}>{baselines.avg_daily_signals}</span>
              </div>
            )}
          </div>
        )}

        {/* Situation assessment (truncated to first sentence) */}
        {assessment && (
          <div style={{ marginBottom: topAction ? 18 : 0 }}>
            <div className="strat-section-title" style={{ marginBottom: 10 }}>
              <FaChartLine size={12} color={T.primary} /> {_t('app.strategy.situationAssessment', { defaultValue: 'Situation Assessment' })}
            </div>
            <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: T.text, direction: 'auto' }}>
              {assessmentExpanded ? assessment : assessmentShort}
            </p>
            {assessmentHasMore && (
              <button className="strat-link" onClick={() => setAssessmentExpanded(v => !v)}>
                {assessmentExpanded ? _t('app.strategy.readLess', { defaultValue: 'Read less' }) : _t('app.strategy.readMore', { defaultValue: 'Read more' })}
              </button>
            )}
          </div>
        )}

        {/* Top action */}
        {topAction && (
          <div style={{ display: 'flex', gap: 12, padding: '16px 18px', borderRadius: 12, background: T.surface, [isRTL ? 'borderRight' : 'borderLeft']: `2px solid ${T.primary}` }}>
            <FaLightbulb size={13} color="#f59e0b" style={{ marginTop: 3, flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: T.primary }}>{_t('app.strategy.topAction', { defaultValue: 'Top Action' })}</span>
              <p style={{ margin: '4px 0 0', fontSize: 14, fontWeight: 600, color: T.text, lineHeight: 1.4, direction: 'auto' }}>{topAction.action}</p>
              {topAction.expected_outcome && (
                <p style={{ margin: '6px 0 0', fontSize: 12, color: T.textMuted, fontStyle: 'italic', direction: 'auto' }}>{_t('app.strategy.expected', { defaultValue: 'Expected:' })} {topAction.expected_outcome}</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Two-column layout: Opportunities + Threats | Competitive */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 28 }}>
        {/* Opportunities & Threats */}
        <div className="strat-card">
          <div className="strat-section-title">
            <FaRocket size={12} color="#30d158" /> {_t('app.strategy.opportunities', { defaultValue: 'Opportunities' })}
          </div>
          {opportunities.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {opportunities.map((opp, i) => (
                <div key={i} className="strat-row" style={{ [isRTL ? 'borderRight' : 'borderLeft']: '2px solid #30d158' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: '#30d158', textTransform: 'uppercase', letterSpacing: 0.5 }}>{opp.urgency}</span>
                  </div>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: T.text, lineHeight: 1.4, direction: 'auto' }}>{opp.title}</p>
                  <p style={{ margin: '6px 0 0', fontSize: 12, color: T.textSec, lineHeight: 1.5, direction: 'auto', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{opp.reasoning}</p>
                  {opp.suggested_action && (
                    <p style={{ margin: '8px 0 0', fontSize: 12, color: '#30d158', fontWeight: 500, direction: 'auto' }}>{arrow} {opp.suggested_action}</p>
                  )}
                </div>
              ))}
            </div>
          ) : <EmptyState text={_t('app.strategy.noOpportunitiesDetected', { defaultValue: 'No opportunities detected yet' })} T={T} />}

          <div className="strat-section-title" style={{ marginTop: 24 }}>
            <FaExclamationTriangle size={12} color="#ff453a" /> {_t('app.strategy.threats', { defaultValue: 'Threats' })}
          </div>
          {threats.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {threats.map((threat, i) => (
                <div key={i} className="strat-row" style={{ [isRTL ? 'borderRight' : 'borderLeft']: '2px solid #ff453a' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: '#ff453a', textTransform: 'uppercase', letterSpacing: 0.5 }}>{threat.severity}</span>
                  </div>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: T.text, lineHeight: 1.4, direction: 'auto' }}>{threat.title}</p>
                  <p style={{ margin: '6px 0 0', fontSize: 12, color: T.textSec, lineHeight: 1.5, direction: 'auto', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{threat.reasoning}</p>
                </div>
              ))}
            </div>
          ) : <EmptyState text={_t('app.strategy.noThreatsDetected', { defaultValue: 'No threats detected' })} T={T} />}
        </div>

        {/* Competitive Position (collapsed by default) */}
        <CollapsibleSection
          T={T}
          icon={<FaCrosshairs size={12} color={T.primary} />}
          title={_t('app.strategy.competitivePosition', { defaultValue: 'Competitive Position' })}
          count={Object.keys(competitive).length}
          defaultOpen={true}
        >
          {Object.keys(competitive).length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {Object.entries(competitive).map(([name, pos]) => (
                <CompetitorCard key={name} name={name} position={pos} T={T} />
              ))}
            </div>
          ) : <EmptyState text={_t('app.strategy.noCompetitorData', { defaultValue: 'No competitor data — add competitor lists to enable' })} T={T} />}

          {briefing?.competitive_summary && (
            <div style={{ marginTop: 16, padding: '12px 14px', borderRadius: 8, background: T.surface }}>
              <p style={{ margin: 0, fontSize: 12, color: T.textSec, lineHeight: 1.5, direction: 'auto' }}>{briefing.competitive_summary}</p>
            </div>
          )}
        </CollapsibleSection>
      </div>

      {/* Recommended Actions */}
      {actions.length > 0 && (
        <div className="strat-card" style={{ marginBottom: 28 }}>
          <div className="strat-section-title">
            <FaLightbulb size={12} color="#f59e0b" /> {_t('app.strategy.recommendedActions', { defaultValue: 'Recommended Actions' })}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {visibleActions.map((action, i) => (
              <div key={i} style={{ display: 'flex', gap: 14, padding: '14px 16px', borderRadius: 10, background: T.surface }}>
                <div style={{ width: 28, height: 28, borderRadius: 14, background: `${T.primary}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: T.primary }}>{action.priority || i + 1}</span>
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: T.text, lineHeight: 1.4, direction: 'auto' }}>{action.action}</p>
                  <p style={{ margin: '4px 0 0', fontSize: 12, color: T.textSec, lineHeight: 1.5, direction: 'auto' }}>{action.reasoning}</p>
                  {action.expected_outcome && (
                    <p style={{ margin: '6px 0 0', fontSize: 11, color: T.textMuted, fontStyle: 'italic', direction: 'auto' }}>{_t('app.strategy.expected', { defaultValue: 'Expected:' })} {action.expected_outcome}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
          {actions.length > 3 && (
            <button className="strat-link" onClick={() => setShowAllActions(v => !v)}>
              {showAllActions ? _t('app.strategy.showLess', { defaultValue: 'Show less' }) : _t('app.strategy.viewAll', { count: actions.length, defaultValue: `View all ${actions.length}` })}
            </button>
          )}
        </div>
      )}

      {/* Performance Review (collapsed by default) */}
      {briefing?.performance_review && (briefing.performance_review.whats_working?.length > 0 || briefing.performance_review.whats_not?.length > 0) && (
        <div style={{ marginBottom: 28 }}>
          <CollapsibleSection
            T={T}
            icon={<FaChartLine size={12} color={T.textMuted} />}
            title={_t('app.strategy.performanceReview', { defaultValue: 'Performance Review' })}
            defaultOpen={false}
          >
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              <div>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#30d158', marginBottom: 8, display: 'block' }}>{_t('app.strategy.working', { defaultValue: 'Working' })}</span>
                {(briefing.performance_review.whats_working || []).map((item, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <FaCheck size={10} color="#30d158" style={{ marginTop: 3, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: T.textSec, lineHeight: 1.5, direction: 'auto' }}>{item}</span>
                  </div>
                ))}
              </div>
              <div>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#ff453a', marginBottom: 8, display: 'block' }}>{_t('app.strategy.needsAttention', { defaultValue: 'Needs Attention' })}</span>
                {(briefing.performance_review.whats_not || []).map((item, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <FaTimes size={10} color="#ff453a" style={{ marginTop: 3, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: T.textSec, lineHeight: 1.5, direction: 'auto' }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </CollapsibleSection>
        </div>
      )}

      {/* Two-column: Patterns | Priorities */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 28 }}>
        {/* Active Patterns */}
        <div className="strat-card">
          <div className="strat-section-title">
            <FaBolt size={12} color="#f59e0b" /> {_t('app.strategy.activePatterns', { defaultValue: 'Active Patterns' })}
          </div>
          {strongPatterns.length > 0 ? (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {visiblePatterns.map(pattern => (
                  <div key={pattern.id} style={{ padding: '12px 14px', borderRadius: 10, background: T.surface }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ fontSize: 10, fontWeight: 600, color: T.textMuted, textTransform: 'uppercase' }}>{(pattern.pattern_type || '').replace(/_/g, ' ')}</span>
                      <button
                        onClick={() => handleDismissPattern(pattern.id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                      >
                        <FaTimes size={10} color={T.textMuted} />
                      </button>
                    </div>
                    <p style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 600, color: T.text, lineHeight: 1.4, direction: 'auto' }}>{pattern.title}</p>
                    {pattern.description && (
                      <p style={{ margin: '0 0 10px', fontSize: 12, color: T.textSec, lineHeight: 1.4, direction: 'auto', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{pattern.description}</p>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div className="strat-strength-bar" style={{ flex: 1 }}>
                        <div className="strat-strength-fill" style={{ width: `${(pattern.strength || 0) * 100}%`, background: pattern.strength > 0.7 ? '#ff453a' : pattern.strength > 0.4 ? '#f59e0b' : T.primary }} />
                      </div>
                      <span style={{ fontSize: 10, color: T.textMuted }}>{pattern.evidence_count} {_t('app.strategy.signals', { defaultValue: 'signals' })}</span>
                    </div>
                  </div>
                ))}
              </div>
              {strongPatterns.length > 4 && (
                <button className="strat-link" onClick={() => setShowAllPatterns(v => !v)}>
                  {showAllPatterns ? _t('app.strategy.showLess', { defaultValue: 'Show less' }) : _t('app.strategy.viewAllPatterns', { count: strongPatterns.length, defaultValue: `View all ${strongPatterns.length} patterns` })}
                </button>
              )}
            </>
          ) : <EmptyState text={_t('app.strategy.noActivePatternsDetected', { defaultValue: 'No active patterns detected' })} T={T} />}
        </div>

        {/* Priorities */}
        <div className="strat-card">
          <div className="strat-section-title">
            <FaShieldAlt size={12} color={T.primary} /> {_t('app.strategy.strategicPriorities', { defaultValue: 'Strategic Priorities' })}
          </div>

          {/* Current priorities */}
          {priorities.current.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              {priorities.current.map((p, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, marginBottom: 6, background: T.surface }}>
                  <div style={{ width: 6, height: 6, borderRadius: 3, background: T.primary, opacity: p.weight || 0.5 }} />
                  <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: T.text }}>{p.name}</span>
                  <span style={{ fontSize: 11, color: T.textMuted }}>{((p.weight || 0.5) * 100).toFixed(0)}%</span>
                  <button onClick={() => handleRemovePriority(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                    <FaTrash size={10} color={T.textMuted} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add priority form */}
          {showAddPriority ? (
            <div style={{ padding: 14, borderRadius: 10, background: T.surface, marginBottom: 16 }}>
              <input className="strat-input" placeholder={_t('app.strategy.priorityName', { defaultValue: 'Priority name' })} value={newPriority.name} onChange={e => setNewPriority(p => ({ ...p, name: e.target.value }))} style={{ marginBottom: 8 }} />
              <input className="strat-input" placeholder={_t('app.strategy.keywords', { defaultValue: 'Keywords (comma-separated)' })} value={newPriority.keywords} onChange={e => setNewPriority(p => ({ ...p, keywords: e.target.value }))} style={{ marginBottom: 8 }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <span style={{ fontSize: 12, color: T.textMuted }}>{_t('app.strategy.weight', { defaultValue: 'Weight:' })}</span>
                <input type="range" min="0.1" max="1" step="0.1" value={newPriority.weight} onChange={e => setNewPriority(p => ({ ...p, weight: parseFloat(e.target.value) }))} style={{ flex: 1 }} />
                <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{(newPriority.weight * 100).toFixed(0)}%</span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="strat-btn" onClick={handleAddPriority} style={{ background: T.primary, color: '#fff', fontSize: 12 }}>
                  <FaCheck size={10} /> {_t('app.strategy.save', { defaultValue: 'Save' })}
                </button>
                <button className="strat-btn" onClick={() => setShowAddPriority(false)} style={{ background: T.surface, color: T.textMuted, border: `1px solid ${T.border}`, fontSize: 12 }}>
                  {_t('app.strategy.cancel', { defaultValue: 'Cancel' })}
                </button>
              </div>
            </div>
          ) : (
            <button className="strat-btn" onClick={() => setShowAddPriority(true)} style={{ background: T.surface, color: T.primary, border: `1px solid ${T.border}`, fontSize: 12, marginBottom: 16 }}>
              <FaPlus size={10} /> {_t('app.strategy.addPriority', { defaultValue: 'Add Priority' })}
            </button>
          )}

          {/* Suggested priorities */}
          {priorities.suggested.length > 0 && (
            <>
              <span style={{ fontSize: 10, fontWeight: 600, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8, display: 'block' }}>{_t('app.strategy.suggested', { defaultValue: 'Suggested' })}</span>
              {priorities.suggested.map((p, i) => (
                <div key={i} className="strat-row" style={{ marginBottom: 8, [isRTL ? 'borderRight' : 'borderLeft']: `2px solid ${T.primary}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{p.name}</span>
                    <button className="strat-btn" onClick={() => handleAcceptPriority(i)} style={{ background: `${T.primary}12`, color: T.primary, fontSize: 11, padding: '6px 12px' }}>
                      <FaCheck size={9} /> {_t('app.strategy.accept', { defaultValue: 'Accept' })}
                    </button>
                  </div>
                  {p.reasoning && <p style={{ margin: '4px 0 0', fontSize: 12, color: T.textSec, lineHeight: 1.4, direction: 'auto' }}>{p.reasoning}</p>}
                </div>
              ))}
            </>
          )}

          {priorities.current.length === 0 && priorities.suggested.length === 0 && (
            <EmptyState text={_t('app.strategy.noPrioritiesSet', { defaultValue: 'No priorities set — add one or wait for suggestions' })} T={T} />
          )}
        </div>
      </div>

      {/* Proactive Moves (collapsed by default) */}
      {briefing?.proactive_moves?.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <CollapsibleSection
            T={T}
            icon={<FaRocket size={12} color={T.primary} />}
            title={_t('app.strategy.proactiveMoves', { defaultValue: 'Proactive Moves' })}
            count={briefing.proactive_moves.length}
            defaultOpen={false}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {briefing.proactive_moves.map((move, i) => (
                <div key={i} style={{ display: 'flex', gap: 12, padding: '10px 14px', borderRadius: 8, background: T.surface }}>
                  <FaArrowUp size={10} color={T.primary} style={{ marginTop: 3 }} />
                  <div>
                    <p style={{ margin: 0, fontSize: 13, color: T.text, lineHeight: 1.4, direction: 'auto' }}>{move.move}</p>
                    {move.timing && <span style={{ fontSize: 11, color: T.textMuted }}>{move.timing}</span>}
                  </div>
                </div>
              ))}
            </div>
          </CollapsibleSection>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          STRATEGIC CONTEXT: Goals, Workflows, Guardrails (configuration)
          ═══════════════════════════════════════════════════════════════════ */}

      <div style={{ borderTop: `1px solid ${T.border}`, marginBottom: 20, paddingTop: 28 }}>
        <h2 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 700, color: T.text, letterSpacing: -0.3 }}>{_t('app.strategy.strategicContext', { defaultValue: 'Strategic Context' })}</h2>
        <p style={{ margin: '0 0 4px', fontSize: 13, color: T.textMuted }}>
          {_t('app.strategy.strategicContextDesc', { defaultValue: 'Define your goals, workflows, and guardrails. The AI uses these to ground its recommendations.' })}
        </p>
      </div>

      {/* Goals (collapsed by default) */}
      <div style={{ marginBottom: 16 }}>
        <CollapsibleSection
          T={T}
          icon={<FaBullseye size={12} color="#30d158" />}
          title={_t('app.strategy.organizationGoals', { defaultValue: 'Organization Goals' })}
          count={goals.length}
          defaultOpen={false}
        >
          {goals.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
              {goals.map(goal => (
                <div key={goal.id} style={{ padding: '14px 16px', borderRadius: 10, background: T.surface }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{goal.name}</span>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => handleUpdateGoalStatus(goal.id, 'achieved')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }} title={_t('app.strategy.markAchieved', { defaultValue: 'Mark achieved' })}>
                        <FaCheck size={10} color="#30d158" />
                      </button>
                      <button onClick={() => handleDeleteGoal(goal.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }} title={_t('app.strategy.delete', { defaultValue: 'Delete' })}>
                        <FaTrash size={10} color={T.textMuted} />
                      </button>
                    </div>
                  </div>
                  {goal.description && <p style={{ margin: '0 0 8px', fontSize: 12, color: T.textSec, lineHeight: 1.4, direction: 'auto' }}>{goal.description}</p>}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
                    <div className="strat-strength-bar" style={{ flex: 1 }}>
                      <div className="strat-strength-fill" style={{ width: `${(goal.progress || 0) * 100}%`, background: '#30d158' }} />
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 600, color: T.textSec }}>{Math.round((goal.progress || 0) * 100)}%</span>
                  </div>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    {goal.target_metric && (
                      <span style={{ fontSize: 11, color: T.textMuted }}>{_t('app.strategy.target', { defaultValue: 'Target:' })} {goal.target_metric.metric} {arrow} {goal.target_metric.target}{goal.target_metric.unit}</span>
                    )}
                    {goal.deadline && (
                      <span style={{ fontSize: 11, color: T.textMuted }}>{_t('app.strategy.deadline', { defaultValue: 'Deadline:' })} {new Date(goal.deadline).toLocaleDateString()}</span>
                    )}
                    <span style={{ fontSize: 10, fontWeight: 600, color: T.textMuted, textTransform: 'uppercase' }}>{goal.goal_type}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {showAddGoal ? (
            <div style={{ padding: 14, borderRadius: 10, background: T.surface, marginBottom: 16 }}>
              <input className="strat-input" placeholder={_t('app.strategy.goalNamePlaceholder', { defaultValue: "Goal name (e.g., 'Increase positive sentiment 20%')" })} value={newGoal.name} onChange={e => setNewGoal(p => ({ ...p, name: e.target.value }))} style={{ marginBottom: 8 }} />
              <input className="strat-input" placeholder={_t('app.strategy.descriptionOptional', { defaultValue: 'Description (optional)' })} value={newGoal.description} onChange={e => setNewGoal(p => ({ ...p, description: e.target.value }))} style={{ marginBottom: 8 }} />
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <select className="strat-input" value={newGoal.goal_type} onChange={e => setNewGoal(p => ({ ...p, goal_type: e.target.value }))} style={{ flex: 1 }}>
                  <option value="general">{_t('app.strategy.goalTypeGeneral', { defaultValue: 'General' })}</option>
                  <option value="sentiment">{_t('app.strategy.goalTypeSentiment', { defaultValue: 'Sentiment' })}</option>
                  <option value="volume">{_t('app.strategy.goalTypeVolume', { defaultValue: 'Volume' })}</option>
                  <option value="engagement">{_t('app.strategy.goalTypeEngagement', { defaultValue: 'Engagement' })}</option>
                  <option value="coverage">{_t('app.strategy.goalTypeCoverage', { defaultValue: 'Coverage' })}</option>
                </select>
                <input className="strat-input" type="date" placeholder={_t('app.strategy.deadlineField', { defaultValue: 'Deadline' })} value={newGoal.deadline} onChange={e => setNewGoal(p => ({ ...p, deadline: e.target.value }))} style={{ flex: 1 }} />
              </div>
              <div style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, marginBottom: 6 }}>{_t('app.strategy.targetMetric', { defaultValue: 'TARGET METRIC (optional)' })}</div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                <input className="strat-input" placeholder={_t('app.strategy.metricName', { defaultValue: 'Metric name' })} value={newGoal.target_metric.metric} onChange={e => setNewGoal(p => ({ ...p, target_metric: { ...p.target_metric, metric: e.target.value } }))} style={{ flex: 2 }} />
                <input className="strat-input" type="number" placeholder={_t('app.strategy.targetValue', { defaultValue: 'Target' })} value={newGoal.target_metric.target} onChange={e => setNewGoal(p => ({ ...p, target_metric: { ...p.target_metric, target: e.target.value } }))} style={{ flex: 1 }} />
                <input className="strat-input" placeholder={_t('app.strategy.unit', { defaultValue: 'Unit' })} value={newGoal.target_metric.unit} onChange={e => setNewGoal(p => ({ ...p, target_metric: { ...p.target_metric, unit: e.target.value } }))} style={{ flex: 1 }} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="strat-btn" onClick={handleAddGoal} style={{ background: T.primary, color: '#fff', fontSize: 12 }}>
                  <FaCheck size={10} /> {_t('app.strategy.saveGoal', { defaultValue: 'Save Goal' })}
                </button>
                <button className="strat-btn" onClick={() => setShowAddGoal(false)} style={{ background: T.surface, color: T.textMuted, border: `1px solid ${T.border}`, fontSize: 12 }}>
                  {_t('app.strategy.cancel', { defaultValue: 'Cancel' })}
                </button>
              </div>
            </div>
          ) : (
            <button className="strat-btn" onClick={() => setShowAddGoal(true)} style={{ background: T.surface, color: '#30d158', border: `1px solid ${T.border}`, fontSize: 12 }}>
              <FaPlus size={10} /> {_t('app.strategy.addGoal', { defaultValue: 'Add Goal' })}
            </button>
          )}

          {goals.length === 0 && !showAddGoal && (
            <EmptyState text={_t('app.strategy.noGoalsSet', { defaultValue: "No goals set — define what you're trying to achieve" })} T={T} />
          )}
        </CollapsibleSection>
      </div>

      {/* Workflows (collapsed by default) */}
      <div style={{ marginBottom: 16 }}>
        <CollapsibleSection
          T={T}
          icon={<FaPlayCircle size={12} color="#f59e0b" />}
          title={_t('app.strategy.workflowsPlaybooks', { defaultValue: 'Workflows & Playbooks' })}
          count={workflows.length}
          defaultOpen={false}
        >
          {workflows.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
              {workflows.map(wf => (
                <div key={wf.id} style={{ padding: '14px 16px', borderRadius: 10, background: T.surface }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{wf.name}</span>
                      <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 4, background: wf.priority_level === 'critical' ? 'rgba(255,69,58,0.1)' : wf.priority_level === 'high' ? 'rgba(245,158,11,0.1)' : T.surface, color: wf.priority_level === 'critical' ? '#ff453a' : wf.priority_level === 'high' ? '#f59e0b' : T.textMuted }}>
                        {wf.priority_level}
                      </span>
                    </div>
                    <button onClick={() => handleDeleteWorkflow(wf.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                      <FaTrash size={10} color={T.textMuted} />
                    </button>
                  </div>
                  {wf.description && <p style={{ margin: '0 0 8px', fontSize: 12, color: T.textSec, lineHeight: 1.4, direction: 'auto' }}>{wf.description}</p>}
                  <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 8 }}>
                    {_t('app.strategy.triggerLabel', { defaultValue: 'Trigger:' })} <span style={{ fontWeight: 500, color: T.textSec }}>{wf.trigger_condition?.type || '—'}</span>
                    {wf.owner_team && <> · {_t('app.strategy.ownerLabel', { defaultValue: 'Owner:' })} <span style={{ fontWeight: 500, color: T.textSec }}>{wf.owner_team}</span></>}
                    {wf.trigger_count > 0 && <> · {_t('app.strategy.triggeredCount', { count: wf.trigger_count, defaultValue: `Triggered ${wf.trigger_count}x` })}</>}
                  </div>
                  {wf.steps?.length > 0 && (
                    <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 8 }}>
                      {wf.steps.slice(0, 4).map((step, i) => (
                        <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
                          <span style={{ fontSize: 11, fontWeight: 600, color: T.primary, width: 16, flexShrink: 0 }}>{step.order || i + 1}.</span>
                          <span style={{ fontSize: 12, color: T.textSec }}>{step.action}{step.owner ? ` (${step.owner})` : ''}</span>
                        </div>
                      ))}
                      {wf.steps.length > 4 && <span style={{ fontSize: 11, color: T.textMuted }}>{_t('app.strategy.moreSteps', { count: wf.steps.length - 4, defaultValue: `+${wf.steps.length - 4} more steps` })}</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {showAddWorkflow ? (
            <div style={{ padding: 14, borderRadius: 10, background: T.surface, marginBottom: 16 }}>
              <input className="strat-input" placeholder={_t('app.strategy.workflowNamePlaceholder', { defaultValue: "Workflow name (e.g., 'Customer Service Crisis')" })} value={newWorkflow.name} onChange={e => setNewWorkflow(p => ({ ...p, name: e.target.value }))} style={{ marginBottom: 8 }} />
              <input className="strat-input" placeholder={_t('app.strategy.descriptionOptional', { defaultValue: 'Description (optional)' })} value={newWorkflow.description} onChange={e => setNewWorkflow(p => ({ ...p, description: e.target.value }))} style={{ marginBottom: 8 }} />
              {/* Friendly trigger selection (raw JSON hidden unless Advanced) */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <select className="strat-input" value={newWorkflow.trigger_type} onChange={e => setNewWorkflow(p => ({ ...p, trigger_type: e.target.value }))} style={{ flex: 1 }}>
                  {TRIGGER_TYPE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{_t(`app.strategy.${opt.labelKey}`, { defaultValue: opt.defaultLabel })}</option>
                  ))}
                </select>
                {newWorkflow.trigger_type === 'pattern_type' && (
                  <select className="strat-input" value={newWorkflow.pattern_type} onChange={e => setNewWorkflow(p => ({ ...p, pattern_type: e.target.value }))} style={{ flex: 1 }}>
                    {PATTERN_TYPE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{_t(`app.strategy.${opt.labelKey}`, { defaultValue: opt.defaultLabel })}</option>
                    ))}
                  </select>
                )}
              </div>
              {(workflowAdvanced || newWorkflow.trigger_type === 'custom') && (
                <input className="strat-input" placeholder={_t('app.strategy.paramsJson', { defaultValue: 'Params JSON (e.g., {"pattern_type":"volume_spike"})' })} value={newWorkflow.trigger_params} onChange={e => setNewWorkflow(p => ({ ...p, trigger_params: e.target.value }))} style={{ marginBottom: 8 }} />
              )}
              {newWorkflow.trigger_type !== 'custom' && (
                <button className="strat-link" onClick={() => setWorkflowAdvanced(v => !v)} style={{ marginBottom: 8, display: 'block' }}>
                  {_t('app.strategy.advancedEditJson', { defaultValue: 'Advanced: edit JSON' })}
                </button>
              )}
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <select className="strat-input" value={newWorkflow.priority_level} onChange={e => setNewWorkflow(p => ({ ...p, priority_level: e.target.value }))} style={{ flex: 1 }}>
                  <option value="low">{_t('app.strategy.priorityLevelLow', { defaultValue: 'Low' })}</option>
                  <option value="medium">{_t('app.strategy.priorityLevelMedium', { defaultValue: 'Medium' })}</option>
                  <option value="high">{_t('app.strategy.priorityLevelHigh', { defaultValue: 'High' })}</option>
                  <option value="critical">{_t('app.strategy.priorityLevelCritical', { defaultValue: 'Critical' })}</option>
                </select>
                <input className="strat-input" placeholder={_t('app.strategy.ownerTeam', { defaultValue: 'Owner team' })} value={newWorkflow.owner_team} onChange={e => setNewWorkflow(p => ({ ...p, owner_team: e.target.value }))} style={{ flex: 1 }} />
                <input className="strat-input" type="number" placeholder={_t('app.strategy.cooldownHours', { defaultValue: 'Cooldown (hrs)' })} value={newWorkflow.cooldown_hours} onChange={e => setNewWorkflow(p => ({ ...p, cooldown_hours: parseInt(e.target.value) || 24 }))} style={{ flex: 1 }} />
              </div>
              <div style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, marginBottom: 6 }}>{_t('app.strategy.steps', { defaultValue: 'STEPS' })}</div>
              {newWorkflow.steps.map((step, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: T.primary, paddingTop: 10, width: 20 }}>{i + 1}.</span>
                  <input className="strat-input" placeholder={_t('app.strategy.stepAction', { defaultValue: 'Action' })} value={step.action} onChange={e => { const steps = [...newWorkflow.steps]; steps[i] = { ...steps[i], action: e.target.value }; setNewWorkflow(p => ({ ...p, steps })); }} style={{ flex: 2 }} />
                  <input className="strat-input" placeholder={_t('app.strategy.stepOwner', { defaultValue: 'Owner' })} value={step.owner} onChange={e => { const steps = [...newWorkflow.steps]; steps[i] = { ...steps[i], owner: e.target.value }; setNewWorkflow(p => ({ ...p, steps })); }} style={{ flex: 1 }} />
                  {i > 0 && <button onClick={() => { const steps = newWorkflow.steps.filter((_, idx) => idx !== i); setNewWorkflow(p => ({ ...p, steps })); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8 }}><FaTimes size={10} color={T.textMuted} /></button>}
                </div>
              ))}
              <button onClick={() => setNewWorkflow(p => ({ ...p, steps: [...p.steps, { order: p.steps.length + 1, action: '', owner: '' }] }))} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: T.primary, fontWeight: 500, padding: '4px 0', marginBottom: 10 }}>
                {_t('app.strategy.addStep', { defaultValue: '+ Add Step' })}
              </button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="strat-btn" onClick={handleAddWorkflow} style={{ background: T.primary, color: '#fff', fontSize: 12 }}>
                  <FaCheck size={10} /> {_t('app.strategy.saveWorkflow', { defaultValue: 'Save Workflow' })}
                </button>
                <button className="strat-btn" onClick={() => setShowAddWorkflow(false)} style={{ background: T.surface, color: T.textMuted, border: `1px solid ${T.border}`, fontSize: 12 }}>
                  {_t('app.strategy.cancel', { defaultValue: 'Cancel' })}
                </button>
              </div>
            </div>
          ) : (
            <button className="strat-btn" onClick={() => setShowAddWorkflow(true)} style={{ background: T.surface, color: '#f59e0b', border: `1px solid ${T.border}`, fontSize: 12 }}>
              <FaPlus size={10} /> {_t('app.strategy.addWorkflow', { defaultValue: 'Add Workflow' })}
            </button>
          )}

          {workflows.length === 0 && !showAddWorkflow && (
            <EmptyState text={_t('app.strategy.noWorkflowsDefined', { defaultValue: 'No workflows defined — add playbooks for recurring situations' })} T={T} />
          )}
        </CollapsibleSection>
      </div>

      {/* Guardrails (collapsed by default) */}
      <div style={{ marginBottom: 28 }}>
        <CollapsibleSection
          T={T}
          icon={<FaShieldAlt size={12} color="#ff453a" />}
          title={_t('app.strategy.guardrailsConstraints', { defaultValue: 'Guardrails & Constraints' })}
          count={guardrails.length}
          defaultOpen={false}
        >
          {guardrails.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              {guardrails.map(gr => (
                <div key={gr.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, background: T.surface }}>
                  <span style={{
                    fontSize: 10, fontWeight: 700, textTransform: 'uppercase', padding: '3px 6px', borderRadius: 4,
                    background: gr.rule_type === 'never' ? 'rgba(255,69,58,0.1)' : gr.rule_type === 'always' ? 'rgba(48,209,88,0.1)' : gr.rule_type === 'escalate' ? 'rgba(245,158,11,0.1)' : 'rgba(10,132,255,0.1)',
                    color: gr.rule_type === 'never' ? '#ff453a' : gr.rule_type === 'always' ? '#30d158' : gr.rule_type === 'escalate' ? '#f59e0b' : T.primary,
                  }}>
                    {gr.rule_type}
                  </span>
                  <span style={{ flex: 1, fontSize: 13, color: T.text }}>{gr.rule_text}</span>
                  {gr.scope !== 'all' && <span style={{ fontSize: 10, color: T.textMuted }}>{gr.scope}</span>}
                  <button onClick={() => handleDeleteGuardrail(gr.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                    <FaTrash size={10} color={T.textMuted} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {showAddGuardrail ? (
            <div style={{ padding: 14, borderRadius: 10, background: T.surface, marginBottom: 16 }}>
              <input className="strat-input" placeholder={_t('app.strategy.rulePlaceholder', { defaultValue: "Rule (e.g., 'Never recommend public engagement on political topics')" })} value={newGuardrail.rule_text} onChange={e => setNewGuardrail(p => ({ ...p, rule_text: e.target.value }))} style={{ marginBottom: 8 }} />
              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                <select className="strat-input" value={newGuardrail.rule_type} onChange={e => setNewGuardrail(p => ({ ...p, rule_type: e.target.value }))} style={{ flex: 1 }}>
                  <option value="never">{_t('app.strategy.ruleTypeNever', { defaultValue: 'Never' })}</option>
                  <option value="always">{_t('app.strategy.ruleTypeAlways', { defaultValue: 'Always' })}</option>
                  <option value="escalate">{_t('app.strategy.ruleTypeEscalate', { defaultValue: 'Escalate' })}</option>
                  <option value="time_window">{_t('app.strategy.ruleTypeTimeWindow', { defaultValue: 'Time Window' })}</option>
                  <option value="require_approval">{_t('app.strategy.ruleTypeRequireApproval', { defaultValue: 'Require Approval' })}</option>
                </select>
                <input className="strat-input" placeholder={_t('app.strategy.scope', { defaultValue: 'Scope (default: all)' })} value={newGuardrail.scope} onChange={e => setNewGuardrail(p => ({ ...p, scope: e.target.value }))} style={{ flex: 1 }} />
                <input className="strat-input" type="number" placeholder={_t('app.strategy.priority', { defaultValue: 'Priority' })} value={newGuardrail.priority} onChange={e => setNewGuardrail(p => ({ ...p, priority: parseInt(e.target.value) || 1 }))} style={{ width: 80 }} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="strat-btn" onClick={handleAddGuardrail} style={{ background: T.primary, color: '#fff', fontSize: 12 }}>
                  <FaCheck size={10} /> {_t('app.strategy.saveGuardrail', { defaultValue: 'Save Guardrail' })}
                </button>
                <button className="strat-btn" onClick={() => setShowAddGuardrail(false)} style={{ background: T.surface, color: T.textMuted, border: `1px solid ${T.border}`, fontSize: 12 }}>
                  {_t('app.strategy.cancel', { defaultValue: 'Cancel' })}
                </button>
              </div>
            </div>
          ) : (
            <button className="strat-btn" onClick={() => setShowAddGuardrail(true)} style={{ background: T.surface, color: '#ff453a', border: `1px solid ${T.border}`, fontSize: 12 }}>
              <FaPlus size={10} /> {_t('app.strategy.addGuardrail', { defaultValue: 'Add Guardrail' })}
            </button>
          )}

          {guardrails.length === 0 && !showAddGuardrail && (
            <EmptyState text={_t('app.strategy.noGuardrailsSet', { defaultValue: 'No guardrails set — define hard constraints for AI recommendations' })} T={T} />
          )}
        </CollapsibleSection>
      </div>
    </div>
  );
}

function CollapsibleSection({ T, icon, title, count, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="strat-card">
      <button className="strat-collapse-head" onClick={() => setOpen(v => !v)}>
        <div className="strat-section-title" style={{ marginBottom: open ? 16 : 0, justifyContent: 'space-between' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {icon} {title}
            {count > 0 && (
              <span style={{ fontSize: 10, fontWeight: 700, color: T.textMuted, background: T.surface, borderRadius: 8, padding: '1px 7px' }}>{count}</span>
            )}
          </span>
          <FaChevronDown size={11} color={T.textMuted} style={{ transform: open ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }} />
        </div>
      </button>
      {open && children}
    </div>
  );
}

function MomentumChip({ label, value, delta, T, isSentiment }) {
  const isPositive = isSentiment ? delta > 0 : (value === 'surging' || value === 'growing' || delta > 0);
  const isNegative = value === 'declining' || delta < -5;
  const color = isPositive ? '#30d158' : isNegative ? '#ff453a' : T.textMuted;
  const Icon = isPositive ? FaArrowUp : isNegative ? FaArrowDown : null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderRadius: 10, background: T.surface, border: `1px solid ${T.border}` }}>
      <span style={{ fontSize: 11, color: T.textMuted }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 700, color, textTransform: 'capitalize' }}>{value}</span>
      {delta !== undefined && delta !== 0 && (
        <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, fontWeight: 600, color }}>
          {Icon && <Icon size={8} />} {delta > 0 ? '+' : ''}{typeof delta === 'number' ? delta.toFixed(1) : delta}%
        </span>
      )}
    </div>
  );
}

function CompetitorCard({ name, position, T }) {
  const sentGap = position.sentiment_gap || 0;
  const engGap = position.engagement_gap_pct || 0;

  const getStatus = (winning, gap) => {
    if (gap === 0) return { label: _t('app.strategy.statusEven', { defaultValue: 'Even' }), color: T.textMuted };
    if (winning) return { label: _t('app.strategy.statusWinning', { defaultValue: 'Winning' }), color: '#30d158' };
    if (winning === false) return { label: _t('app.strategy.statusLosing', { defaultValue: 'Losing' }), color: '#ff453a' };
    if (gap > 0) return { label: _t('app.strategy.statusWinning', { defaultValue: 'Winning' }), color: '#30d158' };
    if (gap < 0) return { label: _t('app.strategy.statusLosing', { defaultValue: 'Losing' }), color: '#ff453a' };
    return { label: _t('app.strategy.statusEven', { defaultValue: 'Even' }), color: T.textMuted };
  };

  const sent = getStatus(position.brand_winning_sentiment, sentGap);
  const eng = getStatus(position.brand_winning_engagement, engGap);

  return (
    <div style={{ padding: '12px 14px', borderRadius: 10, background: T.surface }}>
      <p style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 600, color: T.text }}>{name}</p>
      <div style={{ display: 'flex', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 8, height: 8, borderRadius: 4, background: sent.color }} />
          <span style={{ fontSize: 11, color: T.textSec }}>{_t('app.strategy.sentimentMetric', { defaultValue: 'Sentiment' })}</span>
          <span style={{ fontSize: 11, fontWeight: 600, color: sent.color }}>
            {sent.label} ({sentGap > 0 ? '+' : ''}{sentGap.toFixed(2)})
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 8, height: 8, borderRadius: 4, background: eng.color }} />
          <span style={{ fontSize: 11, color: T.textSec }}>{_t('app.strategy.engagementMetric', { defaultValue: 'Engagement' })}</span>
          <span style={{ fontSize: 11, fontWeight: 600, color: eng.color }}>
            {eng.label} ({engGap > 0 ? '+' : ''}{engGap.toFixed(0)}%)
          </span>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ text, T }) {
  return (
    <div style={{ padding: '20px 0', textAlign: 'center' }}>
      <span style={{ fontSize: 12, color: T.textMuted }}>{text}</span>
    </div>
  );
}
